import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Download, PlayCircle, HardDrive } from "lucide-react";

function humanSize(bytes) {
    if (!bytes) return "—";
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / 1024 / 1024).toFixed(2)} MB`;
}

export default function AdminBackups() {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [running, setRunning] = useState(false);

    const load = () => {
        setLoading(true);
        api.get("/admin/backups").then(({ data }) => setItems(data.items))
            .catch(() => toast.error("Errore caricamento"))
            .finally(() => setLoading(false));
    };
    useEffect(() => { load(); }, []);

    const runNow = async () => {
        setRunning(true);
        try {
            const { data } = await api.post("/admin/backups/run");
            toast.success(`Backup completato (${data.mode}) — ${humanSize(data.size)}`);
            load();
        } catch (err) {
            toast.error(err.response?.data?.detail || "Errore backup");
        } finally { setRunning(false); }
    };

    // Download uses XHR + blob to preserve Authorization header (never expose the JWT in URL).
    const download = async (filename) => {
        try {
            const { data } = await api.get(`/admin/backups/${encodeURIComponent(filename)}`, { responseType: "blob" });
            const url = URL.createObjectURL(data);
            const a = document.createElement("a");
            a.href = url; a.download = filename;
            document.body.appendChild(a); a.click(); a.remove();
            URL.revokeObjectURL(url);
        } catch { toast.error("Download fallito"); }
    };

    return (
        <div data-testid="admin-backups-page">
            <div className="mb-6 flex items-end justify-between">
                <div>
                    <h1 className="text-3xl">Backup</h1>
                    <p className="text-sm text-[hsl(var(--muted-foreground))] mt-1 max-w-lg">
                        Snapshot giornaliero del database Mongo (retention 14 giorni). Automatico
                        alle 03:00, oppure a richiesta.
                    </p>
                </div>
                <button onClick={runNow} disabled={running} className="co-btn-primary" data-testid="backup-run-btn">
                    <PlayCircle className="w-4 h-4" /> {running ? "In corso…" : "Genera backup ora"}
                </button>
            </div>

            {loading && <div className="text-[hsl(var(--muted-foreground))]">Caricamento…</div>}
            {!loading && items.length === 0 && (
                <div className="italic text-[hsl(var(--muted-foreground))]">Nessun backup ancora presente.</div>
            )}

            <div className="space-y-2">
                {items.map((b) => (
                    <div key={b.filename} className="bg-white border border-[hsl(var(--border))] rounded-sm p-4 flex items-center gap-4" data-testid={`backup-row-${b.filename}`}>
                        <HardDrive className="w-4 h-4 text-[hsl(var(--muted-foreground))]" strokeWidth={1.5} />
                        <div className="flex-1 min-w-0">
                            <div className="font-mono text-sm truncate">{b.filename}</div>
                            <div className="text-xs text-[hsl(var(--muted-foreground))]">
                                {humanSize(b.size)} · {new Date(b.created_at).toLocaleString("it-IT")}
                            </div>
                        </div>
                        <button onClick={() => download(b.filename)} className="co-btn-ghost text-xs" data-testid={`backup-download-${b.filename}`}>
                            <Download className="w-3.5 h-3.5" /> Scarica
                        </button>
                    </div>
                ))}
            </div>
        </div>
    );
}
